import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
    installAuthInterceptor,
    refreshAccessToken,
    getTokenExpiry,
    SESSION_EXPIRED_EVENT,
    TOKEN_REFRESHED_EVENT,
} from '../lib/authRefresh';
import { installStepUpInterceptor } from '../lib/stepUp';
import SessionExpiredDialog from '../components/SessionExpiredDialog';
import StepUpDialog from '../components/auth/StepUpDialog';
import DisplayZoneContext from './DisplayZoneContext';
import { resolveDisplayZone } from '../lib/timezone';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Install before the first render so even requests fired during mount
// get silent-refresh-and-retry on 401.
installAuthInterceptor();
// Issue #854: a 403 "Multi-factor authentication required" (a trusted-device
// session on an admin route) opens the StepUpDialog below, then re-sends.
installStepUpInterceptor();

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [token, setToken] = useState(() => localStorage.getItem('crm_token'));
    const [sessionExpired, setSessionExpired] = useState(false);
    // The forced-login credential is deliberately memory-only. A page refresh
    // or tab close discards it, so a password-change token can never become a
    // durable normal session or a localStorage bearer credential.
    const [pendingPasswordChange, setPendingPasswordChange] = useState(null);

    const checkAuth = useCallback(async () => {
        // Handle impersonation token from URL
        const urlParams = new URLSearchParams(window.location.search);
        const impersonateToken = urlParams.get('impersonate_token');
        const impersonateName = urlParams.get('impersonate_name');
        if (impersonateToken) {
            localStorage.setItem('crm_token', impersonateToken);
            localStorage.setItem('crm_impersonating', impersonateName || 'User');
            window.history.replaceState({}, '', window.location.pathname);
            // Fall through to check the token below
        }

        const storedToken = localStorage.getItem('crm_token');
        if (!storedToken) {
            setLoading(false);
            return;
        }

        // Retry logic for transient errors (502/503 during deploys)
        const MAX_RETRIES = 3;
        const RETRY_DELAY = 3000; // 3 seconds between retries

        for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
            try {
                const res = await axios.get(`${API}/auth/me`, {
                    headers: { Authorization: `Bearer ${storedToken}` },
                    timeout: 10000,
                });
                setUser(res.data);
                setToken(storedToken);
                setLoading(false);
                return; // success — exit
            } catch (err) {
                const status = err?.response?.status;
                // On 401, try refreshing the token before giving up
                if (status === 401) {
                    try {
                        const newToken = await refreshAccessToken();
                        if (newToken) {
                            // Retry the /me call with new token
                            const retryRes = await axios.get(`${API}/auth/me`, {
                                headers: { Authorization: `Bearer ${newToken}` },
                                timeout: 10000,
                            });
                            setUser(retryRes.data);
                            setToken(newToken);
                            setLoading(false);
                            return;
                        }
                    } catch {
                        // Refresh failed — clear everything
                    }
                    localStorage.removeItem('crm_token');
                    localStorage.removeItem('crm_refresh_token');
                    localStorage.removeItem('crm_impersonating');
                    setToken(null);
                    setUser(null);
                    setLoading(false);
                    return;
                }
                if (status === 403) {
                    localStorage.removeItem('crm_token');
                    localStorage.removeItem('crm_refresh_token');
                    localStorage.removeItem('crm_impersonating');
                    setToken(null);
                    setUser(null);
                    setLoading(false);
                    return;
                }
                // Transient error (502/503/network) — retry if attempts remain
                if (attempt < MAX_RETRIES) {
                    await new Promise(r => setTimeout(r, RETRY_DELAY));
                } else {
                    // All retries exhausted — keep token, stay "loading" briefly then resolve
                    // so user isn't kicked to login during a deploy
                    setToken(storedToken);
                    setLoading(false);
                }
            }
        }
    }, []);

    // React to the global auth events from the axios interceptor:
    // - token silently refreshed → keep context state in sync
    // - refresh definitively rejected → session is over, show the dialog
    useEffect(() => {
        const onRefreshed = (e) => {
            const newToken = e.detail?.token;
            if (newToken) setToken(newToken);
        };
        const onExpired = () => {
            setToken(null);
            setUser(null);
            setSessionExpired(true);
        };
        window.addEventListener(TOKEN_REFRESHED_EVENT, onRefreshed);
        window.addEventListener(SESSION_EXPIRED_EVENT, onExpired);
        return () => {
            window.removeEventListener(TOKEN_REFRESHED_EVENT, onRefreshed);
            window.removeEventListener(SESSION_EXPIRED_EVENT, onExpired);
        };
    }, []);

    useEffect(() => {
        checkAuth();
    }, [checkAuth]);

    // Proactively refresh the access token a few minutes before it expires so
    // in-app navigation never hits a 401 in the first place. The interceptor
    // remains the backstop (e.g. laptop slept past the timer).
    useEffect(() => {
        if (!token || !user) return;
        if (!localStorage.getItem('crm_refresh_token')) return;
        const expiry = getTokenExpiry(token);
        if (!expiry) return; // not a JWT — no exp to schedule against

        const REFRESH_MARGIN = 5 * 60 * 1000;
        const delay = Math.max(expiry - Date.now() - REFRESH_MARGIN, 10000);
        const timer = setTimeout(() => {
            refreshAccessToken().catch(() => {});
        }, delay);
        return () => clearTimeout(timer);
    }, [token, user]);

    const login = async (email, password) => {
        let res;
        try {
            res = await axios.post(`${API}/auth/login`, { email, password });
        } catch (err) {
            const detail = err?.response?.data?.detail;
            if (
                err?.response?.status === 403 &&
                detail?.code === 'must_change_password' &&
                detail?.password_change_token
            ) {
                setPendingPasswordChange({ token: detail.password_change_token });
                return { password_change_required: true };
            }
            throw err;
        }
        // CASA 3.3.1 / plan §8.1: the response may be a full session
        // ({access_token, refresh_token, user}), an MFA challenge
        // ({mfa_required, mfa_token}) or a forced-enrollment challenge
        // ({mfa_enrollment_required, mfa_token}). Return the raw response so
        // callers (Login.jsx) can branch; only persist tokens when a full
        // session was actually issued.
        const data = res.data;
        if (data.access_token) {
            const { access_token, refresh_token, user: userData } = data;
            localStorage.setItem('crm_token', access_token);
            if (refresh_token) localStorage.setItem('crm_refresh_token', refresh_token);
            setToken(access_token);
            setUser(userData);
            setSessionExpired(false);
        }
        return data;
    };

    // CASA 3.3.1 / issue #557: called by Login.jsx after POST /auth/mfa/verify
    // returns a full session for a pending-token flow. Same invariants as
    // login(): only persist when a full session was actually issued.
    const adoptSession = useCallback(({ access_token, refresh_token, user: userData }) => {
        if (!access_token) return;
        localStorage.setItem('crm_token', access_token);
        if (refresh_token) localStorage.setItem('crm_refresh_token', refresh_token);
        setToken(access_token);
        setUser(userData);
        setSessionExpired(false);
    }, []);

    const completePasswordChange = useCallback(async (newPassword) => {
        if (!pendingPasswordChange?.token) {
            throw new Error('No pending password-change challenge');
        }
        const res = await axios.post(`${API}/auth/change-password`, {
            new_password: newPassword,
        }, {
            headers: { Authorization: `Bearer ${pendingPasswordChange.token}` },
        });
        // The forced endpoint returns a complete session, including the user;
        // only now is it safe to persist a normal access/refresh token pair.
        adoptSession(res.data);
        setPendingPasswordChange(null);
        return res.data;
    }, [adoptSession, pendingPasswordChange]);

    const clearPendingPasswordChange = useCallback(() => {
        setPendingPasswordChange(null);
    }, []);

    const setGoogleUser = (userData) => {
        setUser(userData);
    };

    const logout = async () => {
        try {
            const t = token || localStorage.getItem('crm_token');
            // CASA 2.2.1: send the refresh token so the server can invalidate
            // it along with every other stateful session token for this user.
            await axios.post(`${API}/auth/logout`, {
                refresh_token: localStorage.getItem('crm_refresh_token') || undefined,
            }, {
                headers: t ? { Authorization: `Bearer ${t}` } : {},
                withCredentials: true,
            });
        } catch {}
        localStorage.removeItem('crm_token');
        localStorage.removeItem('crm_refresh_token');
        localStorage.removeItem('crm_impersonating');
        setToken(null);
        setUser(null);
    };

    // D10 / CASA 2.4.1: authenticated password change. The server revokes every
    // other session (token_version bump) and re-issues tokens for THIS device —
    // swap them in so the silent-refresh path keeps working.
    const changePassword = async (currentPassword, newPassword) => {
        const res = await axios.post(`${API}/auth/change-password`, {
            current_password: currentPassword,
            new_password: newPassword,
        }, { headers: getAuthHeader() });
        const { access_token, refresh_token } = res.data;
        if (access_token) {
            localStorage.setItem('crm_token', access_token);
            setToken(access_token);
        }
        if (refresh_token) localStorage.setItem('crm_refresh_token', refresh_token);
        return res.data;
    };

    // "Sign out of all other devices" (plan §11 / §12.3): token_version bump +
    // re-issued tokens for the caller.
    const revokeAllSessions = async () => {
        const res = await axios.post(`${API}/auth/sessions/revoke-all`, {}, { headers: getAuthHeader() });
        const { access_token, refresh_token } = res.data;
        if (access_token) {
            localStorage.setItem('crm_token', access_token);
            setToken(access_token);
        }
        if (refresh_token) localStorage.setItem('crm_refresh_token', refresh_token);
        return res.data;
    };

    const getAuthHeader = useCallback(() => {
        const t = token || localStorage.getItem('crm_token');
        return t ? { Authorization: `Bearer ${t}` } : {};
    }, [token]);

    // #532 — the viewer's display timezone (profile → browser), published on a
    // dependency-free context so timestamp leaves can read it without
    // importing this module's axios/session-dialog tree. See
    // context/DisplayZoneContext.js and hooks/useDisplayZone.js.
    const displayZone = resolveDisplayZone({ user });

    return (
        <AuthContext.Provider value={{ user, loading, token, sessionExpired, login, adoptSession, pendingPasswordChange, completePasswordChange, clearPendingPasswordChange, setGoogleUser, logout, getAuthHeader, changePassword, revokeAllSessions }}>
            <DisplayZoneContext.Provider value={displayZone}>
                {children}
            </DisplayZoneContext.Provider>
            <SessionExpiredDialog
                open={sessionExpired}
                onLoginAgain={() => setSessionExpired(false)}
            />
            <StepUpDialog user={user} />
        </AuthContext.Provider>
    );
}

export const useAuth = () => {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error('useAuth must be used within AuthProvider');
    return ctx;
};
